import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { mspSelectOptions } from './data/mspSelectI.interface';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
	selector: 'msp-select',
	standalone: true,
	imports: [FaIconComponent, CommonModule],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: MspSelectComponent,
			multi: true,
		},
	],
	templateUrl: './msp-select.component.html',
	styleUrl: './msp-select.component.scss',
})
export class MspSelectComponent implements OnInit, OnChanges {
	@ViewChild('mspSelectContainer')
	mspSelectContainer!: ElementRef<HTMLDivElement>;
	@ViewChild('optionContainer') optionContainer!: ElementRef<HTMLDivElement>;

	constructor(private cd: ChangeDetectorRef) {}

	private searchText = '';
	private searchTimeout: NodeJS.Timeout;
	selectContainerSize = 'auto';

	protected readonly icons = {
		faChevronDown: faChevronDown,
	};
	protected noSelectedOptions = true;
	protected collapsed = true;
	protected highlightedOption = -1;
	protected displayValue = '';

	@Input() placeholder = 'Selecione uma opção';

	@Input() options: mspSelectOptions<any>[] = [];

	@Input() disabled = false;

	@Output() onChange: EventEmitter<any[]> = new EventEmitter<any[]>();

	closeClickEventListener: EventListener;

	ngOnInit(): void {
		this.updateDisplayValue();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['options']) {
			this.setOptionContainerSize();
		}
	}

	private updateDisplayValue(): void {
		const selectedOptions = this.options.filter((option) => option.selected);
		if (selectedOptions.length === 0) {
			this.noSelectedOptions = true;
			this.displayValue = this.placeholder;
		} else {
			this.noSelectedOptions = false;
			this.displayValue = selectedOptions.map((option) => option.label).join(', ');
		}
	}

	private getSelectedOption() {
		return this.options.filter((option) => option.selected);
	}

	private setOptionContainerSize(): void {
		if (!this.optionContainer) return;
		this.selectContainerSize = this.optionContainer.nativeElement.offsetWidth + 'px';
	}

	selectOption(option: mspSelectOptions<any>) {
		if (this.disabled) return;
		const oldValues = this.getSelectedOption().map((option) => option.value);
		this.deselectAllOptions();
		const selectedOption = this.options.find((opt) => opt.id === option.id);
		selectedOption.selected = true;
		const newValues = this.getSelectedOption().map((option) => option.value);
		this.collapsed = true;
		if (oldValues == selectedOption.value) return;
		this.updateDisplayValue();
		this.onChangeFn(newValues);
		this.onTouched();
	}

	deselectAllOptions() {
		const options = this.options;
		for (const option of options) option.selected = false;
		this.options = options;
	}

	collapse() {
		if (this.disabled) return;
		this.collapsed = !this.collapsed;

		if (this.collapsed) return;
		this.closeClickEventListener = (e: any) => {
			if (!this.mspSelectContainer.nativeElement.contains(e.target)) {
				this.collapsed = true;
				document.removeEventListener('click', this.closeClickEventListener);
				this.closeClickEventListener = null;
			}
		};
		document.addEventListener('click', this.closeClickEventListener);
		this.highlightedOption = this.options.findIndex((option) => option.selected);
		this.scrollIntoFirstSelected();
	}

	scrollIntoFirstSelected() {
		if (this.highlightedOption === -1) return;
		this.optionContainer?.nativeElement.children[this.highlightedOption].scrollIntoView({ behavior: 'instant', block: 'end' });
	}

	highlightOption(index: number) {
		this.highlightedOption = index;
		this.optionContainer?.nativeElement.children[this.highlightedOption].scrollIntoView({ behavior: 'instant', block: 'nearest' });
	}

	searchOption(event: KeyboardEvent) {
		if (event.key === 'Enter' || event.key == 'ArrowUp' || event.key == 'ArrowDown') return;

		this.searchText += event.key.toLowerCase();
		console.log(this.searchText);
		const optionIndex = this.options.findIndex((option) => option.label.toLowerCase().startsWith(this.searchText));
		if (optionIndex !== -1) {
			this.highlightOption(optionIndex);
		}
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
		this.searchTimeout = setTimeout(() => {
			this.searchText = '';
			this.searchTimeout = null;
		}, 300);
	}

	//Form control functions
	private onChangeFn: (value: any) => void = (value) => {
		this.onChange.emit(value);
	};
	private onTouched: () => void = () => {};

	writeValue(value: any): void {
		if (value) {
			const optionFind = this.options.find((option) => option.value === value);
			if (!optionFind) return;
			optionFind.selected = true;
		}
		this.updateDisplayValue();
	}

	registerOnChange(function_: (value: any) => void): void {
		this.onChangeFn = function_;
	}

	registerOnTouched(function_: () => void): void {
		this.onTouched = function_;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}
}
